import { useEffect } from 'react';
import { Flex, Select } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

interface CustomPaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

/**
 * CustomPagination Component
 *
 * Provides pagination controls with prev/next buttons and page selector.
 * Automatically adjusts current page if it exceeds total pages and
 * disables navigation buttons at boundaries.
 */
const CustomPagination = ({
  page,
  totalPages,
  onPageChange,
}: CustomPaginationProps) => {
  useEffect(() => {
    if (page > totalPages) {
      onPageChange(totalPages);
    }
  }, [totalPages, page, onPageChange]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    onPageChange(newPage);
  };

  return (
    <Flex align='center' gap={12}>
      <Flex
        align='center'
        gap={4}
        style={{ cursor: page === 1 ? 'not-allowed' : 'pointer' }}
        onClick={() => handlePageChange(page - 1)}
      >
        <LeftOutlined
          className={
            page === 1
              ? 'custom-pagination-arrow-disabled'
              : 'custom-pagination-arrow-active'
          }
          style={{
            fontSize: '13px',
            color: page === 1 ? '#ccc' : '#000',
          }}
        />
        <span
          className={
            page === 1
              ? 'custom-pagination-arrow-disabled'
              : 'custom-pagination-arrow-active'
          }
          style={{
            fontSize: '13px',
            fontWeight: '600',
            color: page === 1 ? '#ccc' : '#000',
          }}
        >
          Prev
        </span>
      </Flex>

      <Select
        value={page}
        onChange={handlePageChange}
        popupClassName='custom-dropdown'
        className='custom-pagination-select'
        style={{
          minWidth: '60px',
          textAlign: 'center',
          background: '#f5f5f5',
          borderRadius: '8px',
          fontSize: '13px',
        }}
      >
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
          <Select.Option key={pageNum} value={pageNum}>
            {pageNum}
          </Select.Option>
        ))}
      </Select>

      <span style={{ fontSize: '13px', fontWeight: '600' }}>
        of {totalPages}
      </span>

      <Flex
        align='center'
        gap={4}
        style={{ cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
        onClick={() => handlePageChange(page + 1)}
      >
        <span
          className={
            page === totalPages
              ? 'custom-pagination-arrow-disabled'
              : 'custom-pagination-arrow-active'
          }
          style={{
            fontSize: '13px',
            fontWeight: '600',
            color: page === totalPages ? '#ccc' : '#000',
          }}
        >
          Next
        </span>
        <RightOutlined
          className={
            page === totalPages
              ? 'custom-pagination-arrow-disabled'
              : 'custom-pagination-arrow-active'
          }
          style={{
            fontSize: '13px',
            color: page === totalPages ? '#ccc' : '#000',
          }}
        />
      </Flex>
    </Flex>
  );
};

export default CustomPagination;
